<script lang="ts" setup>
import { useMediaStore } from '@/stores/useMediaStore';
import { storeToRefs } from 'pinia';
import { useConfirm } from 'primevue';
import VideoPlayer from './VideoPlayer.vue';
import BaseButton from './BaseButton.vue';
import BaseIcon from './BaseIcon.vue';
import formatBytes from '@/utils/formatBytes';
import type { MediaType } from '@/utils/getMediaType';

const props = defineProps<{
  type: MediaType;
  src: string;
  name?: string;
  preview?: string;
  width?: 'thumbnail' | 'preview' | 'full';
  unclickable?: boolean;
  downloadUrl?: string;
  fileSize?: number;
  icon?: string;
}>();

const mediaStore = useMediaStore();
const {
  type: typeStore,
  src: srcStore,
  preview: previewStore,
  name: nameStore,
  downloadUrl: downloadUrlStore,
  fileSize: fileSizeStore,
  icon: iconStore,
} = storeToRefs(mediaStore);
const { require } = useConfirm();

function onClick() {
  if (props.unclickable) return;
  typeStore.value = props.type;
  srcStore.value = props.src;
  previewStore.value = props.preview;
  nameStore.value = props.name;
  downloadUrlStore.value = props.downloadUrl;
  fileSizeStore.value = props.fileSize;
  iconStore.value = props.icon;
  require({
    header: props.name,
    message: '',
    acceptLabel: 'Close',
    rejectLabel: '',
    group: 'media-dialog',
  });
}

const canPreview = (type: MediaType) =>
  type === 'image' || type === 'video' || type === 'audio' || type === 'document';
</script>

<template>
  <div
    class="flex items-center justify-center"
    :class="{ 'cursor-pointer': !unclickable }"
    :style="{ width: width === 'full' ? '100%' : width === 'thumbnail' ? '80px' : '128px' }"
    @click="onClick"
  >
    <!-- Thumbnail preview -->
    <div v-if="preview && width !== 'full'" class="flex items-center justify-center">
      <img
        :src="preview"
        alt="Media Preview"
        class="max-w-full max-h-full object-cover rounded-md"
      />
    </div>

    <!-- Full rendering (inside dialog or no thumbnail) -->
    <div v-else-if="canPreview(type)" class="w-full h-full flex items-center justify-center">
      <img
        v-if="type === 'image'"
        :src
        alt="Media Preview"
        class="max-w-full max-h-full object-contain"
      />
      <VideoPlayer v-else-if="type === 'video'" :url="src" />
      <audio v-else-if="type === 'audio'" :src controls class="w-full" />
      <iframe v-else-if="type === 'document'" :src class="w-full h-full min-h-96" />
    </div>

    <!-- Placeholder for non-previewable files -->
    <div
      v-else
      class="flex flex-col items-center justify-center gap-2 p-6 bg-sand-50 rounded-lg w-full"
    >
      <BaseIcon :name="icon ?? 'description'" size="lg" class="text-sand-400" />
      <span v-if="name" class="text-sm text-sand-600 truncate max-w-48 text-center">{{
        name
      }}</span>
      <span v-if="fileSize" class="text-xs text-sand-400">{{ formatBytes(fileSize) }}</span>
      <a v-if="downloadUrl && unclickable" :href="downloadUrl" download @click.stop>
        <BaseButton variant="text" icon="download" label="Download" size="small" />
      </a>
    </div>
  </div>
</template>
